// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { FC, useEffect, useRef, useState } from 'react';
import { CKEditor as Editor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

import { CustomUploadAdapter } from 'utils/customUploadAdapter';

interface IProps {
  value?: string;
  onChange?(value: string): void;
  language?: string;
  disabled?: boolean;
}

/**
 * The `@ckeditor/ckeditor5-react` v6 wrapper + React 18 dev
 * StrictMode double-invocation of effects races the editor's async
 * create/destroy cycle. Symptoms observed on doctor-profile tab
 * switching:
 *   - unmount: "Cannot read properties of null (reading 'model')"
 *     (Watchdog._destroy reads this._editor after the first destroy
 *     nulled it).
 *   - mount:   "datacontroller-init-non-existent-root"
 *     (a second _initializeEditor tries to init the 'main' root
 *     that a still-pending first destroy is about to remove).
 *
 * Both are fixed by giving the editor a stable mount cycle: gate
 * rendering behind a first-effect flag so the wrapper only mounts
 * once per parent lifecycle (skipping StrictMode's discarded first
 * pass), and remount via a per-lifecycle key so subsequent tab
 * switches get a clean instance. Watchdog stays on — it is the
 * standard code path and its own race is neutralised once the
 * caller stops double-mounting.
 */
const CKEditor: FC<IProps> = ({ value, onChange, language, disabled }) => {
  const ref = useRef<ClassicEditor>();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

    return () => setMounted(false);
  }, []);

  if (!mounted) return null;

  return (
    <Editor
      editor={ClassicEditor}
      data={value || ''}
      onReady={editor => {
        const classicEditorInstance = editor as unknown as ClassicEditor;
        ref.current = classicEditorInstance;

        editor.plugins.get('FileRepository').createUploadAdapter = loader => {
          return new CustomUploadAdapter(loader);
        };
      }}
      config={{
        language,
        extraPlugins: [CustomUploadAdapter],
        mediaEmbed: {
          previewsInData: true
        }
      }}
      onChange={() => {
        onChange?.(ref.current?.getData() || '');
      }}
      disabled={disabled}
    />
  );
};

export default CKEditor;
